<script setup lang="ts">
import type { AccessOpinionMode } from './electricityBill.utils'
import type { RegistrationDocument } from '~/services-v2/financial-bff/financial-bff.types'
import FileStorageInput from '~/components/input/FileStorageInput.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  modelValue: string | null | undefined
  projectId: string
  mode: Exclude<AccessOpinionMode, 'HIDDEN'>
  concessionaireName?: string
  error?: string
  disabled?: boolean
  uploadedFile?: RegistrationDocument | null
  enableDelete?: boolean
  accept?: string
}>(), {
  concessionaireName: '',
  accept: 'image/jpeg,image/jpg,image/png,image/webp,application/pdf',
})

defineEmits<{ (e: 'update:modelValue', value: string | null): void }>()

const { t } = useI18n()

const isRequired = computed(() => props.mode === 'SHOW_REQUIRED')

const alertFeedback = computed(() => isRequired.value ? 'warning' : 'informative')

const messagePrefix = computed(() => isRequired.value ? 'required' : 'optional')

const messageSuffix = computed(() => props.concessionaireName ? '' : '_fallback')

const alertTitle = computed(() => t(
  `formalization.electricity_bill.access_opinion.${messagePrefix.value}_title${messageSuffix.value}`,
  { concessionaire: props.concessionaireName },
))

const alertDescription = computed(() => t(
  `formalization.electricity_bill.access_opinion.${messagePrefix.value}_description${messageSuffix.value}`,
  { concessionaire: props.concessionaireName },
))

const fieldError = computed(() => props.error
  ? t('formalization.electricity_bill.access_opinion.required_error')
  : undefined)

const label = computed(() => t(isRequired.value
  ? 'formalization.electricity_bill.access_opinion.label_required'
  : 'formalization.electricity_bill.access_opinion.label_optional'))
</script>

<template>
  <div class="access-opinion flex flex-col items-start gap-4 w-full rounded-lg bg-neutral-high-light p-4">
    <SolAlert
      id="access-opinion-alert"
      :feedback="alertFeedback"
      :title="alertTitle"
    >
      {{ alertDescription }}
    </SolAlert>
    <FileStorageInput
      id="grid_access_approval_document"
      name="grid_access_approval_document"
      class="w-full"
      :model-value="modelValue"
      :project-id="projectId"
      :label="label"
      :disabled="disabled"
      :placeholder="t('formDocument.placeholder')"
      :accept="accept"
      :error="fieldError"
      :uploaded-file="uploadedFile"
      :enable-delete="enableDelete"
      v-bind="$attrs"
      @update:model-value="$emit('update:modelValue', $event)"
    />
  </div>
</template>

<style lang="scss" scoped>
.access-opinion {
  :deep(.sol-upload) {
    @apply bg-neutral-high-pure;
  }
}
</style>
